<template>
  <daily-snapshot-detail v-if="$route.query.snapshot !== undefined" />
  <div v-else id="question_detail" class="question-body">
    <div v-if="cardVisible" class="card">
      <div class="brand-row">
        <span class="coin">●</span>
        <span class="wordmark">happily<span class="tld">.ai</span></span>
      </div>
      <div class="eyebrow">
        {{ $t("questionDetail.QUESTION_DETAILS") }} — {{ typeLabel }}
      </div>

      <!-- loading skeleton -->
      <template v-if="isLoading">
        <div class="skeleton sk-title"></div>
        <div class="skeleton sk-meta"></div>
        <div class="skeleton sk-stat"></div>
        <hr class="rule" />
        <div v-for="i in 4" :key="i" class="skeleton sk-bar"></div>
      </template>

      <template v-else>
        <h1 class="question">{{ questionData.question_name }}</h1>
        <div class="meta">
          {{ dateLabel }}
          <template v-if="totalResponses">
            · {{ totalResponses }}
            {{
              totalResponses > 1
                ? $t("questionDetail.responses")
                : $t("questionDetail.response")
            }}
          </template>
        </div>

        <!-- head-to-head stat: viewer team vs company -->
        <div class="h2h">
          <div v-if="teamStat" class="col">
            <div class="num">
              {{ teamStat.big }}<small v-if="teamStat.unit">{{ teamStat.unit }}</small>
              <span
                v-if="teamStat.delta"
                class="hdelta"
                :class="teamStat.delta.dir"
              >{{ teamStat.delta.arrow }} {{ teamStat.delta.text }}</span>
            </div>
            <div class="lbl">
              <b>{{ teamStat.name }}</b> {{ teamStat.suffix }}
            </div>
          </div>
          <div v-if="teamStat" class="vs"></div>
          <div v-if="companyStat" class="col" :class="{ company: teamStat }">
            <div class="num">
              {{ companyStat.big }}<small v-if="companyStat.unit">{{ companyStat.unit }}</small>
            </div>
            <div class="lbl">
              <b v-if="!teamStat">{{ companyStat.name }}</b>
              <template v-else>{{ companyStat.name }}</template>
              {{ companyStat.suffix }}
            </div>
          </div>
        </div>

        <hr class="rule" />

        <!-- legend (always in bar order: most negative → most positive) -->
        <div class="legend">
          <span v-for="(item, i) in legendItems" :key="i">
            <i class="dot" :class="'color-' + item.color"></i>{{ item.label }}
          </span>
        </div>

        <!-- breakdown rows -->
        <div
          v-for="(item, idx) in rows"
          :key="item.row.id || idx"
          class="row"
          :class="{ overall: idx === 0, viewer: idx === viewerRowIndex }"
        >
          <div class="row-head">
            <div
              class="team"
              :style="{ paddingLeft: item.depth * 14 + 'px' }"
              :title="item.row.team"
            >
              <span v-if="item.depth" class="twig">└ </span
              >{{ item.row.team }}
              <span class="n">· {{ rowN(item.row) }}</span>
            </div>
            <div class="metrics">
              <span class="score">{{ scoreFor(item.row) }}</span>
              <span v-if="idx === 0" class="delta flat">—</span>
              <span
                v-else-if="deltaFor(item.row)"
                class="delta"
                :class="deltaFor(item.row).dir"
              >{{ deltaFor(item.row).arrow }} {{ deltaFor(item.row).text }}</span>
              <span v-else class="delta"></span>
            </div>
          </div>
          <div class="bar">
            <el-tooltip
              v-for="(seg, i) in segmentsFor(item.row)"
              :key="i"
              :content="seg.tooltip"
              placement="top"
            >
              <div
                class="seg"
                :class="'color-' + seg.color"
                :style="{ width: seg.pct + '%' }"
              >
                <span v-if="seg.pct > 7">{{ seg.pct }}%</span>
              </div>
            </el-tooltip>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import DailySnapshotDetail from "./daily-snapshot-detail.vue";
import CryptoJS from "crypto-js";
import dateFormat from "date-fns/format";
import * as fmt from "./question-details/format";

const SCALE_BUCKETS = ["0–6", "7–8", "9–10"];

export default {
  name: "daily-insights-detail",
  components: { DailySnapshotDetail },
  data() {
    return {
      email: "",
      quest_id: "",
      dateInput: "",
    };
  },
  mounted() {
    if (this.$route.query.snapshot !== undefined) return;
    if (this.$route.params.id) {
      let bytes = CryptoJS.AES.decrypt(
        this.$route.params.id,
        "HappilyDailyDetail"
      );
      let originalText = bytes.toString(CryptoJS.enc.Utf8);
      if (originalText) {
        let codeSplit = originalText.split("||");
        this.email = codeSplit[0];
        this.quest_id = codeSplit[1];
        this.dateInput = codeSplit[2];
        this.getDataDetail();
      }
    }
  },
  computed: {
    questionData() {
      return this.$store.getters["report/questionData"];
    },
    answerOpt() {
      return this.$store.getters["report/answerOpt"];
    },
    yesnoCard() {
      return this.$store.getters["report/yesnoCard"];
    },
    multipleCard() {
      return this.$store.getters["report/multipleCard"];
    },
    scaleCard() {
      return this.$store.getters["report/scaleCard"];
    },
    datamultichoices() {
      return this.$store.getters["report/datamultichoices"];
    },
    countmultichoices() {
      return this.$store.getters["report/countmultichoices"];
    },
    yes() {
      return this.$store.getters["report/yes"];
    },
    scaleCardLoading() {
      return this.$store.getters["report/scaleCardLoading"];
    },
    yesnoCardLoading() {
      return this.$store.getters["report/yesnoCardLoading"];
    },
    multipleCardLoading() {
      return this.$store.getters["report/multipleCardLoading"];
    },
    userData() {
      return this.$store.getters["report/userData"];
    },
    teamHeight() {
      return this.$store.getters["report/teamHeight"];
    },
    cardVisible() {
      return this.scaleCard || this.yesnoCard || this.multipleCard;
    },
    isLoading() {
      return (
        this.scaleCardLoading ||
        this.yesnoCardLoading ||
        this.multipleCardLoading
      );
    },
    typeKey() {
      if (this.scaleCard) return "scale";
      if (this.yesnoCard) return "yesno";
      return "multiple";
    },
    typeLabel() {
      if (this.typeKey === "scale") return this.$t("questionDetail.Scale");
      if (this.typeKey === "yesno") return this.$t("questionDetail.Yes_No");
      return this.$t("questionDetail.Multiple_Choice");
    },
    dateLabel() {
      if (!this.dateInput) return "";
      const d = new Date(this.dateInput);
      if (isNaN(d.getTime())) return this.dateInput;
      return dateFormat(d, "ddd, MMM D, YYYY");
    },
    overallRow() {
      return (this.datamultichoices && this.datamultichoices[0]) || null;
    },
    // Company-wide count — the store's countmultichoices only sums the viewer's
    // team, which reads wrong next to the company row's own n.
    totalResponses() {
      return fmt.rowCount(this.overallRow) || this.countmultichoices;
    },
    viewerRow() {
      return (this.datamultichoices && this.datamultichoices[1]) || null;
    },
    // Viewer manages the whole company: the viewer row duplicates the overall
    // row, so collapse the head-to-head and skip the duplicate in the list.
    isCompanyViewer() {
      return !!(
        this.userData &&
        this.teamHeight &&
        this.userData.team === this.teamHeight
      );
    },
    rows() {
      return fmt.flattenRows(this.datamultichoices, this.isCompanyViewer);
    },
    viewerRowIndex() {
      return this.isCompanyViewer ? -1 : 1;
    },
    positiveLabel() {
      return this.answerOpt[fmt.posIndex(this.yes)] || "";
    },
    // m_choice.weight aligned to the store's value[] ordering — the input to
    // the same weighted score the daily report emails show.
    mcWeights() {
      const q = this.questionData;
      return fmt.alignedMcWeights(q && q.m_choice ? q.m_choice.weight : null);
    },
    negativeLabel() {
      return this.answerOpt[1 - fmt.posIndex(this.yes)] || "";
    },
    companyStat() {
      return this.statFor(this.overallRow, null);
    },
    teamStat() {
      // Hidden when the viewer is the company or their row has no data —
      // the header then shows the single company stat.
      if (this.isCompanyViewer) return null;
      if (!this.viewerRow || !fmt.rowCount(this.viewerRow)) return null;
      return this.statFor(this.viewerRow, this.overallRow);
    },
    legendItems() {
      if (this.typeKey === "scale") {
        return SCALE_BUCKETS.map((label, i) => ({
          label,
          color: fmt.SCALE_COLOR_ORDER[i],
        }));
      }
      if (this.typeKey === "yesno") {
        return [
          { label: this.negativeLabel, color: 2 },
          { label: this.positiveLabel, color: 4 },
        ];
      }
      return this.mcColorSlots.map((color, i) => ({
        label: fmt.mcLabelFor(i, this.answerOpt),
        color,
      }));
    },
    // Weight-credit colors so a zero-weight option can't render green;
    // positional ramp only when a question has no usable weights.
    mcColorSlots() {
      const slots = fmt.mcCreditColorSlots(this.mcWeights);
      return slots.length ? slots : fmt.mcColorOrder(this.answerOpt.length);
    },
  },
  watch: {
    questionData() {
      document.title = "Question Details - " + this.questionData.question_name;
    },
  },
  methods: {
    getDataDetail() {
      if (this.email && this.dateInput && this.quest_id)
        this.$store.dispatch("report/getDataQuestionDetail", {
          user: this.email,
          quest_id: this.quest_id,
          date: this.dateInput,
          loading: "DailyDetail",
        });
    },
    rowN(row) {
      return fmt.rowCount(row);
    },
    // Head-to-head stat block. `reference` (the overall row) is passed only for
    // the viewer's team, which is the only side that carries a delta chip.
    statFor(row, reference) {
      if (!row) return null;
      if (this.typeKey === "scale") {
        const has = typeof row.avg === "number" && fmt.rowCount(row) > 0;
        return {
          big: has ? row.avg.toFixed(1) : "–",
          unit: has ? "/10" : "",
          name: row.team,
          suffix: this.$t("questionDetail.average"),
          delta: reference
            ? this.chip(fmt.avgDelta(row.avg, reference.avg), "")
            : null,
        };
      }
      if (this.typeKey === "yesno") {
        const s = fmt.yesNoShares(row.value, this.yes);
        const ref = reference
          ? fmt.yesNoShares(reference.value, this.yes)
          : null;
        return {
          big: s.n ? s.posPct + "%" : "–",
          unit: "",
          name: row.team,
          suffix: "· " + this.positiveLabel,
          delta:
            ref && s.n && ref.n
              ? this.chip(fmt.ptsDelta(s.posPct, ref.posPct), "pt")
              : null,
        };
      }
      // Multiple choice: the weighted score the daily report emails show
      // (each answer contributes its option's weight), so the header compares
      // the same number managers already saw in their email.
      const p = fmt.mcWeightedPct(row.value, this.mcWeights);
      const rp = reference
        ? fmt.mcWeightedPct(reference.value, this.mcWeights)
        : null;
      return {
        big: p === null ? "–" : p + "%",
        unit: "",
        name: row.team,
        suffix: "· " + this.$t("questionDetail.score"),
        delta:
          p !== null && rp !== null
            ? this.chip(fmt.ptsDelta(p, rp), "pt")
            : null,
      };
    },
    chip(delta, unit) {
      if (delta === null || delta === 0) return null;
      return {
        dir: delta > 0 ? "up" : "down",
        arrow: delta > 0 ? "▲" : "▼",
        text: Math.abs(delta) + unit,
      };
    },
    scoreFor(row) {
      if (this.typeKey === "scale") {
        return typeof row.avg === "number" && fmt.rowCount(row) > 0
          ? row.avg.toFixed(1)
          : "–";
      }
      if (this.typeKey === "yesno") {
        const s = fmt.yesNoShares(row.value, this.yes);
        return s.n ? s.posPct + "%" : "–";
      }
      const p = fmt.mcWeightedPct(row.value, this.mcWeights);
      return p === null ? "–" : p + "%";
    },
    deltaFor(row) {
      if (!this.overallRow) return null;
      if (this.typeKey === "scale") {
        if (!(typeof row.avg === "number" && fmt.rowCount(row) > 0)) {
          return null;
        }
        return this.chip(fmt.avgDelta(row.avg, this.overallRow.avg), "");
      }
      if (this.typeKey === "yesno") {
        const s = fmt.yesNoShares(row.value, this.yes);
        const ref = fmt.yesNoShares(this.overallRow.value, this.yes);
        if (!s.n || !ref.n) return null;
        return this.chip(fmt.ptsDelta(s.posPct, ref.posPct), "pt");
      }
      const p = fmt.mcWeightedPct(row.value, this.mcWeights);
      const rp = fmt.mcWeightedPct(this.overallRow.value, this.mcWeights);
      if (p === null || rp === null) return null;
      return this.chip(fmt.ptsDelta(p, rp), "pt");
    },
    // Bar segments: [{pct, color, tooltip}], zero-width segments dropped.
    // Order is always most-negative → most-positive, left to right.
    segmentsFor(row) {
      const out = [];
      if (this.typeKey === "scale") {
        const range = row.range || [];
        const total = fmt.sum(range);
        for (let i = 0; i < range.length; i++) {
          const p = fmt.pct(range[i], total);
          if (p) {
            out.push({
              pct: p,
              color: fmt.SCALE_COLOR_ORDER[i],
              tooltip: SCALE_BUCKETS[i] + " · " + p + "%",
            });
          }
        }
        return out;
      }
      if (this.typeKey === "yesno") {
        const s = fmt.yesNoShares(row.value, this.yes);
        if (s.negPct) {
          out.push({
            pct: s.negPct,
            color: 2,
            tooltip: this.negativeLabel + " · " + s.negPct + "%",
          });
        }
        if (s.posPct) {
          out.push({
            pct: s.posPct,
            color: 4,
            tooltip: this.positiveLabel + " · " + s.posPct + "%",
          });
        }
        return out;
      }
      const value = row.value || [];
      const total = fmt.sum(value);
      const slots = this.mcColorSlots;
      for (let i = 0; i < value.length; i++) {
        const p = fmt.pct(value[i], total);
        if (p) {
          out.push({
            pct: p,
            color: slots[i],
            tooltip: fmt.mcLabelFor(i, this.answerOpt) + " · " + p + "%",
          });
        }
      }
      return out;
    },
  },
};
</script>

<style lang="scss" scoped>
.question-body {
  background-color: #f4f4f6;
  min-height: 100vh;
  padding: 40px 16px 64px;
  box-sizing: border-box;
}
.card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.06);
  max-width: 780px;
  margin: 0 auto;
  padding: 28px 32px 36px;
  color: #2a2a2a;
}
.brand-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 900;
  color: #4c32cc;
  margin-bottom: 20px;
  .coin {
    color: #ffc200;
    font-size: 15px;
  }
  .tld {
    opacity: 0.6;
  }
}
.eyebrow {
  font-size: 11px;
  letter-spacing: 0.14em;
  font-weight: 700;
  color: #999;
  text-transform: uppercase;
}
.question {
  font-size: 22px;
  font-weight: 900;
  line-height: 1.3;
  margin: 6px 0 4px;
  word-break: break-word;
}
.meta {
  font-size: 13px;
  color: #777;
}
.h2h {
  display: flex;
  align-items: stretch;
  gap: 22px;
  margin: 18px 0 6px;
  .col {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .num {
    font-size: 40px;
    font-weight: 900;
    line-height: 1;
    small {
      font-size: 16px;
      color: #999;
      font-weight: 700;
    }
  }
  .col.company .num {
    color: rgba(42, 42, 42, 0.45);
  }
  .lbl {
    font-size: 12.5px;
    color: #777;
    margin-top: 6px;
  }
  .vs {
    width: 1px;
    background: #eaeaea;
    flex-shrink: 0;
  }
  .hdelta {
    font-size: 13px;
    font-weight: 700;
    margin-left: 6px;
    &.up {
      color: #0aa861;
    }
    &.down {
      color: #e03a3a;
    }
  }
}
.rule {
  border: none;
  border-top: 1px solid #eaeaea;
  margin: 16px 0 12px;
}
.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  font-size: 11.5px;
  color: #555;
  margin-bottom: 10px;
  span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
  .dot {
    width: 10px;
    height: 10px;
    border-radius: 3px;
    display: inline-block;
    flex-shrink: 0;
  }
}
.row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 8px;
  border-radius: 8px;
  margin: 2px -8px;
  &.overall {
    background: #f6f5fb;
    .team {
      font-weight: 700;
    }
  }
  &.viewer .team {
    font-weight: 700;
  }
}
.row-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 320px;
  flex-shrink: 0;
}
.team {
  font-size: 13px;
  color: #444;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  .twig {
    color: #ccc;
  }
  .n {
    color: #aaa;
    font-size: 11px;
  }
}
.metrics {
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-shrink: 0;
  .score {
    font-size: 13.5px;
    font-weight: 900;
    min-width: 30px;
    text-align: right;
  }
  .delta {
    font-size: 10.5px;
    font-weight: 700;
    min-width: 36px;
    &.up {
      color: #0aa861;
    }
    &.down {
      color: #e03a3a;
    }
    &.flat {
      color: #bbb;
    }
  }
}
.bar {
  flex: 1;
  display: flex;
  height: 28px;
  border-radius: 6px;
  overflow: hidden;
  background: #f2f2f2;
  min-width: 0;
}
.seg {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  overflow: hidden;
  span {
    white-space: nowrap;
  }
}
.seg.color-1,
.dot.color-1 {
  background-color: #b32e2e;
}
.seg.color-2,
.dot.color-2 {
  background-color: #ff4343;
}
.seg.color-3,
.dot.color-3 {
  background-color: #ffc200;
}
.seg.color-3 {
  color: #2a2a2a;
}
.seg.color-4,
.dot.color-4 {
  background-color: #0dd379;
}
.seg.color-5,
.dot.color-5 {
  background-color: #00b383;
}
.seg.color-6,
.dot.color-6 {
  background-color: #068462;
}
.seg.color-7,
.dot.color-7 {
  background-color: #f99002;
}
.seg.color-8,
.dot.color-8 {
  background-color: #ff6b6b;
}
.skeleton {
  border-radius: 6px;
  background: linear-gradient(90deg, #f2f2f2 25%, #e9e9e9 37%, #f2f2f2 63%);
  background-size: 400% 100%;
  animation: shimmer 1.4s ease infinite;
}
@keyframes shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}
.sk-title {
  height: 24px;
  width: 70%;
  margin: 10px 0 8px;
}
.sk-meta {
  height: 13px;
  width: 40%;
  margin-bottom: 18px;
}
.sk-stat {
  height: 44px;
  width: 55%;
}
.sk-bar {
  height: 28px;
  margin: 8px 0;
}
@media (max-width: 639px) {
  .question-body {
    padding: 16px 10px 40px;
  }
  .card {
    padding: 20px 16px 28px;
    border-radius: 10px;
  }
  .question {
    font-size: 18px;
  }
  .h2h {
    gap: 16px;
    .num {
      font-size: 32px;
      small {
        font-size: 13px;
      }
    }
  }
  .row {
    display: block;
    padding: 8px 8px 10px;
  }
  .row-head {
    width: auto;
    margin-bottom: 5px;
  }
  .bar {
    height: 24px;
  }
}
</style>
